import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Heart, Award, Send, Loader2, Search, Trophy, Star,
  Handshake, Lightbulb, Crown, Rocket, Puzzle, GraduationCap,
  Users, ChevronRight, Sparkles, RefreshCw, BarChart3, AlertTriangle, TrendingUp, Mail
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

const BADGE_ICONS = {
  teamwork: Handshake,
  innovation: Lightbulb,
  leadership: Crown,
  helpfulness: Heart,
  above_beyond: Rocket,
  problem_solver: Puzzle,
  mentor: GraduationCap,
  customer_champion: Star,
};

const BADGE_COLORS = {
  teamwork: 'bg-blue-100 text-blue-700 border-blue-200',
  innovation: 'bg-violet-100 text-violet-700 border-violet-200',
  leadership: 'bg-amber-100 text-amber-700 border-amber-200',
  helpfulness: 'bg-red-100 text-red-700 border-red-200',
  above_beyond: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  problem_solver: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  mentor: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  customer_champion: 'bg-yellow-100 text-yellow-700 border-yellow-200',
};

const BadgeIcon = ({ badge, className = "h-5 w-5" }) => {
  const Icon = BADGE_ICONS[badge] || Award;
  return <Icon className={className} />;
};

const KudosCard = ({ kudos }) => {
  const colorClass = BADGE_COLORS[kudos.badge] || 'bg-slate-100 text-slate-700';
  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(dateStr).toLocaleDateString();
  };

  // Milestone celebration card
  if (kudos.type === 'milestone') {
    return (
      <div className="p-4 border-2 rounded-xl bg-gradient-to-br from-amber-50 to-white hover:shadow-lg transition-all"
        style={{ borderColor: kudos.badge_color || '#ca8a04' }}
        data-testid={`milestone-wall-${kudos.id}`}>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: `${kudos.badge_color}20` }}>
            <Trophy className="h-6 w-6" style={{ color: kudos.badge_color }} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className="text-xs text-white" style={{ backgroundColor: kudos.badge_color }}>{kudos.badge_label}</Badge>
            </div>
            <p className="text-sm font-semibold text-slate-800 mt-1">{kudos.recipient_name}</p>
            <p className="text-sm text-slate-600 mt-1">{kudos.message}</p>
            <p className="text-xs text-slate-400 mt-1">{timeAgo(kudos.created_at)}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 border rounded-xl hover:shadow-md transition-all bg-white" data-testid={`kudos-card-${kudos.id}`}>
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${colorClass}`}>
          <BadgeIcon badge={kudos.badge} className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-slate-800">{kudos.sender_name}</span>
            <ChevronRight className="h-3 w-3 text-slate-400" />
            <span className="text-sm font-semibold text-slate-800">{kudos.recipient_name}</span>
          </div>
          <Badge className={`${colorClass} border mt-1 text-xs`}>
            <BadgeIcon badge={kudos.badge} className="h-3 w-3 mr-1" />
            {kudos.badge_label}
          </Badge>
          <p className="text-sm text-slate-600 mt-2 leading-relaxed">{kudos.message}</p>
          <p className="text-xs text-slate-400 mt-2">{timeAgo(kudos.created_at)}</p>
        </div>
      </div>
    </div>
  );
};

const SendKudosDialog = ({ open, onClose, employees, onSent }) => {
  const [badges, setBadges] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedBadge, setSelectedBadge] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [empSearch, setEmpSearch] = useState('');

  useEffect(() => {
    api.get('/kudos/badges').then(r => setBadges(r.data)).catch(() => {});
  }, []);

  const filtered = employees.filter(e =>
    e.name?.toLowerCase().includes(empSearch.toLowerCase()) ||
    e.department?.toLowerCase().includes(empSearch.toLowerCase())
  );

  const send = async () => {
    if (!selectedEmployee || !selectedBadge || !message.trim()) {
      toast.error('Please fill all fields');
      return;
    }
    setSending(true);
    try {
      await api.post('/kudos', {
        recipient_employee_id: selectedEmployee,
        badge: selectedBadge,
        message: message.trim(),
      });
      toast.success('Kudos sent!');
      setSelectedEmployee('');
      setSelectedBadge('');
      setMessage('');
      onClose();
      onSent?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send kudos');
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-indigo-500" /> Send Recognition
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {/* Employee Search */}
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Colleague</label>
            <Input placeholder="Search by name..." value={empSearch}
              onChange={(e) => setEmpSearch(e.target.value)} className="mb-2" autoComplete="off" data-testid="kudos-employee-search" />
            <div className="max-h-32 overflow-y-auto border rounded-lg">
              {filtered.slice(0, 8).map(emp => (
                <div key={emp.id}
                  className={`p-2 cursor-pointer text-sm border-b last:border-0 transition-colors ${
                    selectedEmployee === emp.id ? 'bg-indigo-50 text-indigo-700' : 'hover:bg-slate-50'
                  }`}
                  onClick={() => { setSelectedEmployee(emp.id); setEmpSearch(emp.name); }}
                  data-testid={`kudos-select-${emp.id}`}>
                  <p className="font-medium">{emp.name}</p>
                  <p className="text-xs text-slate-500">{emp.department || 'No Dept'}</p>
                </div>
              ))}
              {filtered.length === 0 && <p className="p-2 text-xs text-slate-400">No employees found</p>}
            </div>
          </div>

          {/* Badge Selection */}
          <div>
            <label className="text-sm font-medium text-slate-700 mb-2 block">Badge</label>
            <div className="grid grid-cols-4 gap-2">
              {badges.map(b => {
                const colorClass = BADGE_COLORS[b.id] || 'bg-slate-100';
                return (
                  <button key={b.id}
                    className={`p-2 rounded-lg border-2 text-center transition-all ${
                      selectedBadge === b.id ? 'border-indigo-500 shadow-md scale-105' : 'border-transparent hover:border-slate-200'
                    } ${colorClass}`}
                    onClick={() => setSelectedBadge(b.id)}
                    data-testid={`badge-${b.id}`}>
                    <BadgeIcon badge={b.id} className="h-5 w-5 mx-auto" />
                    <p className="text-[10px] mt-1 font-medium leading-tight">{b.label}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Message */}
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Message</label>
            <Textarea value={message} onChange={(e) => setMessage(e.target.value)}
              placeholder="Share why this person deserves recognition..." maxLength={300}
              className="min-h-[80px]" data-testid="kudos-message" />
            <p className="text-xs text-slate-400 text-right mt-1">{message.length}/300</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={send} disabled={sending || !selectedEmployee || !selectedBadge || !message.trim()}
            className="bg-indigo-600 hover:bg-indigo-700" data-testid="send-kudos-btn">
            {sending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
            Send Kudos
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const PIE_COLORS = ['#6366f1', '#2563eb', '#059669', '#d97706', '#dc2626', '#7c3aed', '#0891b2', '#ca8a04'];

const TeamAnalytics = ({ analytics }) => {
  const { summary, badge_distribution, department_received, weekly_trends, top_givers, top_receivers, recognition_gaps } = analytics;
  const [digestPreview, setDigestPreview] = useState(null);
  const [digestLoading, setDigestLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const previewDigest = async () => {
    setDigestLoading(true);
    try {
      const res = await api.get('/kudos/digest/preview');
      setDigestPreview(res.data);
    } catch (err) { toast.error('Failed to load digest preview'); }
    finally { setDigestLoading(false); }
  };

  const sendDigest = async () => {
    setSending(true);
    try {
      const res = await api.post('/kudos/digest/send');
      toast.success(`Digest sent to ${res.data.sent} employees for ${res.data.month}`);
      setDigestPreview(null);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send digest'); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-indigo-600">{summary.total_kudos}</p>
          <p className="text-xs text-slate-500">Total Kudos</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-emerald-600">{summary.this_month}</p>
          <p className="text-xs text-slate-500">This Month</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-blue-600">{summary.unique_givers}</p>
          <p className="text-xs text-slate-500">Unique Givers</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-violet-600">{summary.unique_receivers}</p>
          <p className="text-xs text-slate-500">Unique Receivers</p>
        </CardContent></Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Badge Distribution Pie */}
        <Card data-testid="badge-distribution-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Award className="h-4 w-4 text-violet-500" /> Badge Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            {badge_distribution.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={badge_distribution} dataKey="count" nameKey="label" cx="50%" cy="50%" outerRadius={90} label={({ label, count }) => `${label} (${count})`}>
                    {badge_distribution.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : <p className="text-sm text-slate-400 text-center py-8">No data</p>}
          </CardContent>
        </Card>

        {/* Weekly Trends Bar */}
        <Card data-testid="weekly-trends-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><TrendingUp className="h-4 w-4 text-blue-500" /> Weekly Activity</CardTitle>
          </CardHeader>
          <CardContent>
            {weekly_trends.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={weekly_trends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#6366f1" radius={[4, 4, 0, 0]} name="Kudos" />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-sm text-slate-400 text-center py-8">No weekly data</p>}
          </CardContent>
        </Card>

        {/* Department Breakdown */}
        <Card data-testid="dept-breakdown-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Users className="h-4 w-4 text-emerald-500" /> Kudos by Department (Received)</CardTitle>
          </CardHeader>
          <CardContent>
            {department_received.length > 0 ? (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={department_received} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                  <YAxis dataKey="department" type="category" tick={{ fontSize: 11 }} width={100} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#059669" radius={[0, 4, 4, 0]} name="Received" />
                </BarChart>
              </ResponsiveContainer>
            ) : <p className="text-sm text-slate-400 text-center py-8">No department data</p>}
          </CardContent>
        </Card>

        {/* Top Givers & Receivers */}
        <Card data-testid="top-givers-receivers">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Star className="h-4 w-4 text-amber-500" /> Top Givers & Receivers</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-xs font-semibold text-emerald-600 uppercase mb-2">Top Givers</p>
              {top_givers.slice(0, 5).map((g, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 border-b last:border-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-400 w-4">{i + 1}</span>
                    <span className="text-sm">{g.name}</span>
                  </div>
                  <Badge variant="outline" className="text-xs">{g.count} sent</Badge>
                </div>
              ))}
            </div>
            <div>
              <p className="text-xs font-semibold text-indigo-600 uppercase mb-2">Top Receivers</p>
              {top_receivers.slice(0, 5).map((r, i) => (
                <div key={i} className="flex items-center justify-between py-1.5 border-b last:border-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-400 w-4">{i + 1}</span>
                    <span className="text-sm">{r.name}</span>
                  </div>
                  <Badge variant="outline" className="text-xs">{r.count} received</Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recognition Gaps */}
      {recognition_gaps.length > 0 && (
        <Card data-testid="recognition-gaps">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-500" /> Recognition Gaps (No kudos in 90 days)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {recognition_gaps.slice(0, 12).map((e, i) => (
                <div key={i} className="p-2 bg-amber-50 rounded-lg border border-amber-100">
                  <p className="text-sm font-medium text-slate-700">{e.name}</p>
                  <p className="text-xs text-slate-500">{e.department || 'No Dept'}</p>
                </div>
              ))}
            </div>
            {recognition_gaps.length > 12 && (
              <p className="text-xs text-slate-400 mt-2 text-center">+{recognition_gaps.length - 12} more employees</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Monthly Digest */}
      <Card className="border-indigo-200" data-testid="digest-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2"><Mail className="h-4 w-4 text-indigo-500" /> Monthly Recognition Digest</CardTitle>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={previewDigest} disabled={digestLoading} data-testid="preview-digest-btn">
                {digestLoading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Search className="h-4 w-4 mr-1" />}
                Preview
              </Button>
              <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={sendDigest} disabled={sending} data-testid="send-digest-btn">
                {sending ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
                Send to All
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {digestPreview ? (
            <div className="space-y-3 p-3 bg-slate-50 rounded-lg border">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-slate-700">{digestPreview.month}</p>
                <Badge variant="outline" className="text-xs">{digestPreview.summary.total_kudos} kudos</Badge>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="p-2 bg-white rounded border"><p className="text-lg font-bold text-indigo-600">{digestPreview.summary.total_kudos}</p><p className="text-[10px] text-slate-500">Given</p></div>
                <div className="p-2 bg-white rounded border"><p className="text-lg font-bold text-emerald-600">{digestPreview.summary.unique_givers}</p><p className="text-[10px] text-slate-500">Givers</p></div>
                <div className="p-2 bg-white rounded border"><p className="text-lg font-bold text-amber-600">{digestPreview.summary.unique_receivers}</p><p className="text-[10px] text-slate-500">Receivers</p></div>
              </div>
              {digestPreview.top_receivers.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-600 mb-1">Top Recognized</p>
                  {digestPreview.top_receivers.slice(0, 3).map((r, i) => (
                    <div key={i} className="flex justify-between text-sm py-1"><span>{i + 1}. {r.name}</span><Badge variant="outline" className="text-xs">{r.count}</Badge></div>
                  ))}
                </div>
              )}
              {digestPreview.popular_badges.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-600 mb-1">Popular Badges</p>
                  <div className="flex flex-wrap gap-1">
                    {digestPreview.popular_badges.map((b, i) => (
                      <Badge key={i} variant="outline" className="text-xs">{b.label} ({b.count})</Badge>
                    ))}
                  </div>
                </div>
              )}
              {digestPreview.milestones_achieved.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-slate-600 mb-1">Milestones</p>
                  {digestPreview.milestones_achieved.map((m, i) => (
                    <p key={i} className="text-sm">{m.name} — <span className="font-medium">{m.tier}</span> ({m.title})</p>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-4">
              <Mail className="h-8 w-8 mx-auto text-slate-300 mb-2" />
              <p className="text-sm text-slate-500">Preview or send the monthly recognition digest to all employees</p>
              <p className="text-xs text-slate-400 mt-1">Auto-scheduled on the 1st of each month at 10:00 AM IST</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const KudosWall = ({ user }) => {
  const [activeTab, setActiveTab] = useState('wall');
  const [wall, setWall] = useState([]);
  const [received, setReceived] = useState([]);
  const [sent, setSent] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const isAdmin = user?.role === 'admin' || user?.role === 'hr_manager';

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [wallRes, empRes] = await Promise.all([
        api.get('/kudos/wall'),
        api.get('/employees?limit=500'),
      ]);
      setWall(wallRes.data.kudos || []);
      const empData = empRes.data;
      setEmployees(Array.isArray(empData) ? empData : empData.employees || empData.data || []);

      // Load personal data (may fail for admin without employee profile)
      try {
        const [recvRes, sentRes, statsRes, lbRes] = await Promise.all([
          api.get('/kudos/received'),
          api.get('/kudos/sent'),
          api.get('/kudos/my-stats'),
          api.get('/kudos/leaderboard'),
        ]);
        setReceived(recvRes.data || []);
        setSent(sentRes.data || []);
        setStats(statsRes.data);
        setLeaderboard(lbRes.data || []);
      } catch { /* Admin without employee profile */ }

      // Load analytics for admin/manager
      try {
        const analyticsRes = await api.get('/kudos/analytics');
        setAnalytics(analyticsRes.data);
      } catch { /* non-admin */ }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  if (loading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="space-y-6" data-testid="kudos-wall-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Recognition Wall</h1>
            <p className="text-sm text-slate-500">Celebrate your colleagues' achievements</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={loadAll}><RefreshCw className="h-4 w-4 mr-1" /> Refresh</Button>
            <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={() => setSendDialogOpen(true)} data-testid="open-send-kudos">
              <Sparkles className="h-4 w-4 mr-1" /> Send Kudos
            </Button>
          </div>
        </div>

        {/* Stats Row */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Card className="border-l-4 border-l-indigo-500">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                  <Award className="h-5 w-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-indigo-600">{stats.received_count}</p>
                  <p className="text-xs text-slate-500">Received</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-emerald-500">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Send className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-emerald-600">{stats.sent_count}</p>
                  <p className="text-xs text-slate-500">Sent</p>
                </div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-amber-500">
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Trophy className="h-5 w-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-600">{wall.length}</p>
                  <p className="text-xs text-slate-500">Total Kudos</p>
                </div>
              </CardContent>
            </Card>
            {stats.top_badge && (
              <Card className="border-l-4 border-l-violet-500">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${BADGE_COLORS[stats.top_badge.id] || 'bg-slate-100'}`}>
                    <BadgeIcon badge={stats.top_badge.id} className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-violet-600">{stats.top_badge.label}</p>
                    <p className="text-xs text-slate-500">Top Badge</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-100">
            <TabsTrigger value="wall" className="data-[state=active]:bg-white" data-testid="kudos-wall-tab">
              <Users className="mr-2 h-4 w-4" /> Wall
            </TabsTrigger>
            <TabsTrigger value="received" className="data-[state=active]:bg-white" data-testid="kudos-received-tab">
              <Award className="mr-2 h-4 w-4" /> Received ({received.length})
            </TabsTrigger>
            <TabsTrigger value="sent" className="data-[state=active]:bg-white" data-testid="kudos-sent-tab">
              <Send className="mr-2 h-4 w-4" /> Sent ({sent.length})
            </TabsTrigger>
            <TabsTrigger value="leaderboard" className="data-[state=active]:bg-white" data-testid="kudos-leaderboard-tab">
              <Trophy className="mr-2 h-4 w-4" /> Leaderboard
            </TabsTrigger>
            {isAdmin && (
              <TabsTrigger value="analytics" className="data-[state=active]:bg-white" data-testid="kudos-analytics-tab">
                <BarChart3 className="mr-2 h-4 w-4" /> Team Analytics
              </TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="wall" className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {wall.length > 0 ? wall.map(k => <KudosCard key={k.id} kudos={k} />) : (
                <Card className="md:col-span-2 p-8 text-center">
                  <Sparkles className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">No kudos yet. Be the first to recognize someone!</p>
                </Card>
              )}
            </div>
          </TabsContent>

          <TabsContent value="received" className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {received.length > 0 ? received.map(k => <KudosCard key={k.id} kudos={k} />) : (
                <Card className="md:col-span-2 p-8 text-center">
                  <Award className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">No kudos received yet</p>
                </Card>
              )}
            </div>
          </TabsContent>

          <TabsContent value="sent" className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {sent.length > 0 ? sent.map(k => <KudosCard key={k.id} kudos={k} />) : (
                <Card className="md:col-span-2 p-8 text-center">
                  <Send className="h-12 w-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">You haven't sent any kudos yet</p>
                </Card>
              )}
            </div>
          </TabsContent>

          <TabsContent value="leaderboard" className="mt-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><Trophy className="h-4 w-4 text-amber-500" /> Top Recognized Employees</CardTitle>
              </CardHeader>
              <CardContent>
                {leaderboard.length > 0 ? (
                  <div className="space-y-2">
                    {leaderboard.map((entry, i) => (
                      <div key={entry.employee_id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                            i === 0 ? 'bg-amber-100 text-amber-700' : i === 1 ? 'bg-slate-200 text-slate-700' : i === 2 ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-500'
                          }`}>
                            {i + 1}
                          </div>
                          <div>
                            <p className="text-sm font-semibold">{entry.name}</p>
                            <p className="text-xs text-slate-500">{entry.department || 'No Dept'}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Award className="h-4 w-4 text-amber-500" />
                          <span className="font-bold text-sm">{entry.kudos_count}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 text-center py-4">No kudos data yet</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {isAdmin && analytics && (
            <TabsContent value="analytics" className="mt-4" data-testid="analytics-content">
              <TeamAnalytics analytics={analytics} />
            </TabsContent>
          )}
        </Tabs>
      </div>

      <SendKudosDialog open={sendDialogOpen} onClose={() => setSendDialogOpen(false)}
        employees={employees} onSent={loadAll} />
    </Layout>
  );
};

export default KudosWall;
